import type { ApiClient } from "./client";

export interface InvoiceSummary {
  id: string; billId: string; branchId: string; docType: "invoice" | "bill_of_supply"; number: string; fy: string; issueDate: string; currency: string;
  /** Null in `GET /invoices` without `revenue.bill_amounts`; the document itself is never redacted. */
  taxableTotal: number | null; cgstTotal: number | null; sgstTotal: number | null; igstTotal: number | null; roundOff: number | null; total: number | null;
}
export interface DocumentLine {
  billLineId: string; description: string; hsnSac: string | null; quantity: number; unitPrice: number; taxRateBp: number;
  taxableValue: number; cgst: number; sgst: number; igst: number; total: number;
}
/** The frozen document the server stored at issue (R6). Rendered as is. */
export interface InvoiceDocument {
  schemaVersion: 1; docType: "invoice" | "bill_of_supply" | "credit_note"; id: string; billId: string; number: string; fy: string; issueDate: string; currency: string;
  seller: { legalName: string; taxId: string | null; branchName: string; address: unknown; stateCode: string | null };
  buyer: { name: string; legalName: string | null; taxId: string | null };
  placeOfSupply: string | null;
  lines: DocumentLine[];
  taxSummary: { taxRateBp: number; taxableValue: number; cgst: number; sgst: number; igst: number }[];
  totals: { taxable: number; cgst: number; sgst: number; igst: number; roundOff: number; total: number };
  notes?: string | null;
  invoiceNumber?: string; invoiceDate?: string; reason?: string;
}
export interface CreditNoteSummary {
  id: string; invoiceId: string; billId: string; number: string; fy: string; issueDate: string; reason: string;
  /** Money figures are null in `GET /credit-notes` without `revenue.bill_amounts`. */
  settlement: "reduce_balance" | "member_credit" | "refund"; taxableTotal: number | null; total: number | null; toBalance: number | null; toSettlement: number | null;
}

export const listInvoices = (c: ApiClient, q: { from?: string; to?: string; branchId?: string; cursor?: string } = {}) =>
  c.get<{ invoices: InvoiceSummary[]; nextCursor: string | null }>("/invoices", { limit: 50, ...q });
export const getInvoice = (c: ApiClient, id: string) => c.get<InvoiceDocument>(`/invoices/${id}`);
export const getBillInvoice = (c: ApiClient, billId: string) => c.get<InvoiceDocument>(`/bills/${billId}/invoice`);
export const exportInvoices = (c: ApiClient, q: { from?: string; to?: string; cursor?: string } = {}) =>
  c.get<{ invoices: InvoiceSummary[]; nextCursor: string | null }>("/invoices/export", { limit: 500, ...q });

export const listCreditNotes = (c: ApiClient, q: { cursor?: string } = {}) =>
  c.get<{ creditNotes: CreditNoteSummary[]; nextCursor: string | null }>("/credit-notes", { limit: 50, ...q });
export const getCreditNote = (c: ApiClient, id: string) => c.get<InvoiceDocument>(`/credit-notes/${id}`);
export const createCreditNote = (c: ApiClient, v: {
  billId: string; lines: { billLineId: string; amountExTax?: number; sessions?: number }[]; reason: string;
  settlement: "reduce_balance" | "member_credit" | "refund"; refundMethod?: "cash" | "gateway"; refundPaymentId?: string;
}, idempotencyKey: string) => c.post<{ creditNote: CreditNoteSummary & { refundId: string | null; balance: number } }>("/credit-notes", v, { idempotencyKey });
